import React from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';

import SectionContainer from '../SectionContainer';
import css from './SectionHero.module.css';
import previewPng from './preview.png';
import { Button, Logo, NamedLink, PrimaryButton, SecondaryButton } from '../../../../components';
import { FormattedMessage } from 'react-intl';
import { useStore } from 'react-redux';
import { useRouteMatch, useLocation, useHistory } from 'react-router-dom';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../../util/search';
import { createResourceLocatorString } from '../../../../util/routes';
import TopbarSearchForm from '../../../TopbarContainer/Topbar/TopbarSearchForm/TopbarSearchForm';
import { useConfiguration } from '../../../../context/configurationContext';
import { parse } from '../../../../util/urlHelpers';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component for a website's hero section
 * The Section Hero doesn't have any Blocks by default, all the configurations are made in the Section Hero settings
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'hero'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionHero = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    options,
  } = props;
  const config = useConfiguration();
  const user = useStore()?.getState()?.user?.currentUser;
  const pathname = useRouteMatch();
  const routeConfiguration = useRouteConfiguration();
  const isLandingPage = pathname?.isExact && pathname?.url === '/';
  const userOnLanding = isLandingPage && user;
  const location = useLocation();
  const { keywords, address, origin, bounds } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const history = useHistory();

  const handleSubmit = values => {
    const { currentSearchParams } = props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };
    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
    };
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
  };
  const topbarSearcInitialValues = () => {
    if (isMainSearchTypeKeywords(config)) {
      return { keywords };
    }

    // Only render current search if full place object is available in the URL params
    const locationFieldsPresent = isOriginInUse(config)
      ? address && origin && bounds
      : address && bounds;
    return {
      location: locationFieldsPresent
        ? {
            search: address,
            selectedPlace: { address, origin, bounds },
          }
        : null,
    };
  };
  const initialSearchFormValues = topbarSearcInitialValues();

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={classNames(rootClassName || css.root)}
      appearance={appearance}
      options={fieldOptions}
      outsideContent={
        <>
          <div className={css.previewImageWrapper}>
            <img src={previewPng} alt="" className={css.previewImage} />
          </div>
          <div className={css.gradientBottom} />
        </>
      }
    >
      {hasHeaderFields ? (
        <header className={classNames(css.sectionDetails)}>
          <div className={css.logoWrapper}>
            <Logo />
          </div>
          <div className={css.headersWrapper}>
            <Field data={title} className={css.title} options={fieldOptions} />
            <Field data={description} className={css.description} options={fieldOptions} />
            {config && (
              <TopbarSearchForm
                onSubmit={handleSubmit}
                initialValues={initialSearchFormValues}
                isMobile={false}
              />
            )}
          </div>

          {userOnLanding ? null : (
            <div className={css.buttonsWrapper}>
              <NamedLink
                href="/signup/parent"
                name="SignupForParent"
                activeClassName={css.activeLink}
              >
                <Button style={{ padding: 0, minHeight: 40, minWidth: 200 }} onClick={() => {}}>
                  <FormattedMessage id="Cappa.HomepageLeftButton" defaultMessage={"I'm a parent"} />
                </Button>
              </NamedLink>
              <NamedLink
                href="/signup/careProvider"
                name="SignupForCareProvider"
                activeClassName={css.activeLink}
              >
                <SecondaryButton
                  style={{ padding: 0, minHeight: 40, minWidth: 200 }}
                  onClick={() => {}}
                >
                  <FormattedMessage
                    id="Cappa.HomepageRightButton"
                    defaultMessage={"I'm a Care provider"}
                  />
                </SecondaryButton>
              </NamedLink>
            </div>
          )}
        </header>
      ) : null}
    </SectionContainer>
  );
};

export default SectionHero;
